import { Button, Col, Form, Modal, Row } from 'antd';
import { RuleObject } from 'antd/es/form';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import CheckboxComponent from 'components/atoms/CheckboxComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import PrilohyTable from 'components/molecules/Dokumenty/PrilohyTable';
import FormWrapper from 'components/molecules/FormWrapper';

import useProfily from 'hooks/useProfily';

import { IZmenregiPrikaz, PrikazZmenregiGet, PrikazZmenregiPut } from 'services/dokumenty/prikazy';
import { ISmlouvniStrany, dokumentIsinApi, dokumentOsobyApi } from 'services/dokumenty/smlouva';

import { EEvidenceCp } from 'enums/isin/evidenceCp';

import { validateICO } from 'utils/validations/icoValidation';
import { validateLEI } from 'utils/validations/leiValidation';

import Osoby from '../Osoby';
import Podpisy from '../Podpisy';
import { getDataIsins } from './Zruseni';

interface IZmenregiFirstStep {
    nextStep: () => void;
    zadostID: number;
    prikazyData: ISmlouvniStrany;
    data?: IZmenregiPrikaz;
    setData: React.Dispatch<React.SetStateAction<IZmenregiPrikaz | undefined>>;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    loading: boolean;
    signingDisabled: boolean;
}

const ZmenregiFirstStep: React.FC<IZmenregiFirstStep> = ({
    nextStep,
    zadostID,
    prikazyData,
    data,
    setData,
    setLoading,
    loading,
    signingDisabled
}) => {
    const { setLevel } = useProfily();
    const { dokumenty } = useContext(Context);
    const [form] = Form.useForm();
    const [, rerender] = useState<boolean>(false);
    const [dataIsins] = useState<
        {
            labelcz: string;
            value: string;
        }[]
    >(
        getDataIsins(dokumenty).map(e => {
            return {
                labelcz: e.isin,
                value: e.isin
            };
        }) || []
    );

    useEffect(() => {
        form.setFieldsValue({
            souhlas: data?.isin ? true : false,
            souhlas2: data?.isin ? true : false,
            souhlas3: data?.isin ? true : false,
            souhlas4: data?.isin ? true : false
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (!dataIsins.length) {
            dokumentIsinApi().then(resp => {
                setLevel('dokumenty', {
                    ...dokumenty,
                    dokumentyPrava: resp,
                    dokumentyMenuReady: true
                });
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <LoadingWrapper loading={loading}>
            {data && prikazyData && (
                <FormWrapper
                    outForm={form}
                    onFinish={values => {
                        setLoading(true);
                        const dataToSend = {
                            ...(values as any),
                            datum: values.datum.format('YYYY-MM-DD')
                        };
                        delete dataToSend.idmbSpolecnost;
                        delete dataToSend.dicdphSpolecnost;
                        delete dataToSend.nazevSpolecnost;
                        delete dataToSend.leiSpolecnost;
                        delete dataToSend.souhlas2;
                        delete dataToSend.souhlas3;
                        delete dataToSend.souhlas4;
                        PrikazZmenregiPut(zadostID, dataToSend)
                            .then(() => {
                                setData(dataToSend);
                                nextStep();
                            })
                            .finally(() => setLoading(false));
                    }}
                >
                    <Title text="EMITENT" level={5} />
                    <InputComponent
                        disabled={true}
                        initialValue={prikazyData?.spolecnost.idmb}
                        label="IDENTIFIKACNI_CISLO"
                        name="idmbSpolecnost"
                        notRequired={true}
                    />
                    {prikazyData?.spolecnost.dicdph && (
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.dicdph}
                            label="ISIN_IDENTIFIKACE_DIC"
                            name="dicdphSpolecnost"
                            notRequired={true}
                        />
                    )}
                    <InputComponent
                        disabled={true}
                        initialValue={prikazyData?.spolecnost.nazev}
                        label="ISIN_IDENTIFIKACE_FIRMA"
                        name="nazevSpolecnost"
                        notRequired={true}
                    />
                    <InputComponent
                        disabled={true}
                        initialValue={prikazyData?.spolecnost.lei}
                        label="LEI_CELE"
                        name="leiSpolecnost"
                        notRequired={true}
                    />
                    <SelectComponent
                        name="isin"
                        labelParagraph={'PRIKAZ_ZNIZENI_ISIN'}
                        defaultValue={data?.isin}
                        data={dataIsins}
                        isFormItem={true}
                        disabled={signingDisabled}
                    />
                    <DatePickerComponent
                        name="datum"
                        initialValue={data?.datum ? dayjs(data.datum) : undefined}
                        //  disabledDate={current => current < dayjs().add(6, 'days')}
                        label="PRIKAZ_ZMENREGI_DATUM"
                        infoTooltipText="PRIKAZ_ZRUSENI_DATE_TOOLTIP"
                        onChange={val =>
                            form.setFieldsValue({
                                kedni: val.subtract(1, 'day')
                            })
                        }
                        disabled={signingDisabled}
                    />
                    <Paragraph
                        text="PRIKAZ_ZMENREGI_DATUM_TEXT"
                        style={{
                            display: 'block',
                            marginBottom: 20
                        }}
                    />
                    <SelectComponent
                        name="osobap"
                        labelParagraph={'PRIKAZ_ZMENREGI_MISTO_PUV'}
                        defaultValue={data?.osobap}
                        data={[
                            {
                                labelcz: 'CDCP_LONG',
                                value: 'CDCP'
                            },
                            {
                                labelcz: 'DLT_LONG',
                                value: 'CDCPDLT'
                            }
                        ]}
                        isFormItem={true}
                        onChange={() => rerender(pv => !pv)}
                        disabled={signingDisabled}
                    />

                    {form.getFieldValue('osobap') === EEvidenceCp.JINY && (
                        <>
                            <InputComponent
                                label="PRIKAZ_ZRUSENI_PRIDELENI_OSOBA_PUV_JINY_ICOLEI"
                                name="jinaosobap"
                                initialValue={data?.jinaosobap}
                                type="text"
                                maxLength={20}
                                rules={[
                                    () => ({
                                        async validator(_rule: RuleObject, value: string) {
                                            if (validateLEI(value) === 0 || validateICO(value) === 0 || !value)
                                                return Promise.resolve();
                                            return Promise.reject(
                                                <TranslateText>
                                                    {value.length > 8 ? 'NEVALIDNI_LEI' : 'NEVALIDNI_ICO'}
                                                </TranslateText>
                                            );
                                        }
                                    })
                                ]}
                                disabled={signingDisabled}
                            />
                        </>
                    )}
                    <SelectComponent
                        name="osoban"
                        labelParagraph={'PRIKAZ_ZMENREGI_MISTO_NOVE'}
                        defaultValue={data?.osoban}
                        data={[
                            {
                                labelcz: 'CDCP_LONG',
                                value: 'CDCP'
                            },
                            {
                                labelcz: 'DLT_LONG',
                                value: 'CDCPDLT'
                            }
                        ]}
                        isFormItem={true}
                        disabled={signingDisabled}
                        onChange={() => rerender(pv => !pv)}
                    />

                    {form.getFieldValue('osoban') === EEvidenceCp.JINY && (
                        <InputComponent
                            label="PRIKAZ_ZRUSENI_PRIDELENI_OSOBA_NOVY_JINY_ICOLEI"
                            name="jinaosoban"
                            initialValue={data?.jinaosoban}
                            type="text"
                            maxLength={20}
                            rules={[
                                () => ({
                                    async validator(_rule: RuleObject, value: string) {
                                        if (validateLEI(value) === 0 || validateICO(value) === 0 || !value)
                                            return Promise.resolve();
                                        return Promise.reject(
                                            <TranslateText>
                                                {value.length > 8 ? 'NEVALIDNI_LEI' : 'NEVALIDNI_ICO'}
                                            </TranslateText>
                                        );
                                    }
                                })
                            ]}
                            disabled={signingDisabled}
                        />
                    )}
                    <CheckboxComponent
                        name="souhlas2"
                        checkboxText={<TranslateText>PRIKAZ_ZMENREGI_CHKBOX_TEXT1</TranslateText>}
                        style={{
                            marginBottom: 4
                        }}
                        disabled={signingDisabled}
                    />
                    <CheckboxComponent
                        name="souhlas3"
                        checkboxText={<TranslateText>PRIKAZ_ZMENREGI_CHKBOX_TEXT2</TranslateText>}
                        style={{
                            marginBottom: 4
                        }}
                        disabled={signingDisabled}
                    />
                    <CheckboxComponent
                        name="souhlas4"
                        checkboxText={<TranslateText>PRIKAZ_ZMENREGI_CHKBOX_TEXT3</TranslateText>}
                        style={{
                            marginBottom: 4
                        }}
                        disabled={signingDisabled}
                    />

                    <PrilohyTable zadostID={zadostID} required={true} disabled={signingDisabled} />
                    <Title level={5} text="PRIKAZ_ZRUSENI_INFO_TITLE" />
                    <Paragraph
                        text={'PRIKAZ_ZMENREGI_TEXT1'}
                        style={{
                            display: 'block',
                            marginTop: 10,
                            marginBottom: 10
                        }}
                    />

                    <Paragraph
                        text={'PRIKAZ_ZMENREGI_TEXT2'}
                        style={{
                            display: 'block',
                            marginTop: 10,
                            marginBottom: 10
                        }}
                    />

                    <Row
                        style={{
                            marginTop: 26
                        }}
                    >
                        <CheckboxComponent
                            name="souhlas"
                            checkboxText={<TranslateText>POTVRZENI_CHECKBOX</TranslateText>}
                            disabled={signingDisabled}
                            // onChange={() => setIsChecked2(pv => !pv)}
                        />
                    </Row>
                    <Row className="button-row">
                        <Col>
                            <ButtonComponent
                                buttontext="POKRACOVAT"
                                id="isinPokracovatBtn"
                                // disabled={!isChecked2}
                                htmlType="submit"
                            />
                        </Col>
                    </Row>
                </FormWrapper>
            )}
        </LoadingWrapper>
    );
};

interface IZmenregi {
    zadostID: number;
    prikazyData: ISmlouvniStrany;
}

const Zmenregi: React.FC<IZmenregi> = ({ zadostID, prikazyData }) => {
    const { confirm } = Modal;
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [data, setData] = useState<IZmenregiPrikaz>();
    const [loading, setLoading] = useState<boolean>(false);
    const signingDisabled = !!data?.souhlas;

    useEffect(() => {
        setLoading(true);
        PrikazZmenregiGet(zadostID)
            .then(resp => {
                dokumentOsobyApi(zadostID, true)
                    .then(response => response.osoby.some(e => !!e.podepsano) && setCurrentStep(2))
                    .finally(() => setLoading(false));
                setData(resp);
            })
            .catch(() => {
                setData({} as IZmenregiPrikaz);
                setLoading(false);
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostID]);

    const next = () => {
        window.scrollTo(0, 0);
        setCurrentStep(currentStep + 1);
    };

    const prev = () => {
        window.scrollTo(0, 0);
        setCurrentStep(currentStep - 1);
    };

    const steps = [
        {
            title: '',
            content: (
                <ZmenregiFirstStep
                    nextStep={next}
                    zadostID={zadostID!}
                    prikazyData={prikazyData}
                    data={data}
                    setData={setData}
                    setLoading={setLoading}
                    loading={loading}
                    signingDisabled={signingDisabled}
                />
            )
        },
        {
            title: '',
            content: <Osoby nextStep={next} prevStep={prev} zadostID={zadostID!} isDodatek={false} />
        },
        {
            title: '',
            content: <Podpisy prevStep={prev} zadostID={zadostID!} showPreControl={false} isPrikaz={true} />
        }
    ];
    return (
        <LoadingWrapper loading={loading}>
            {steps.length - 2 > currentStep && data?.souhlas && (
                <Button
                    style={{
                        top: '30%',
                        right: 10,
                        position: 'fixed',
                        zIndex: 999
                    }}
                    type="primary"
                    onClick={() =>
                        confirm({
                            title: <TranslateText>DOKUMENTY_OSOBY_ZMENIT</TranslateText>,
                            cancelText: <TranslateText>NE</TranslateText>,
                            okText: <TranslateText>ANO</TranslateText>,
                            onOk() {
                                setLoading(true);
                                PrikazZmenregiPut(zadostID!, {
                                    ...data!,
                                    souhlas: false
                                })
                                    .then(() => {
                                        setData({
                                            ...data!,
                                            souhlas: false
                                        });
                                    })
                                    .finally(() => setLoading(false));
                            }
                        })
                    }
                >
                    <TranslateText>POVOLIT_EDITACI</TranslateText>
                </Button>
            )}
            <Row gutter={8}>
                <Col span={24}>{steps[currentStep].content}</Col>
            </Row>
        </LoadingWrapper>
    );
};

export default Zmenregi;
